import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(response.data);
      setStatus(response.data.status);
      setNotes(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(
        `${BACKEND_URL}/api/applications/${id}`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Updated successfully');
    } catch (error) {
      console.error('Error updating application:', error);
      alert('Error updating application');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center font-manrope">Loading...</div>;
  }

  if (!application) {
    return <div className="text-center font-manrope">Application not found</div>;
  }

  return (
    <div data-testid="application-detail">
      <div className="flex items-center gap-4 mb-8">
        <Button
          onClick={() => navigate('/admin/dashboard')}
          variant="ghost"
          data-testid="back-button"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back
        </Button>
        <h1 className="text-4xl font-unbounded font-black">Application Detail</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card p-6 border border-border">
            <h2 className="text-2xl font-unbounded font-bold mb-6">Personal Information</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-1">Full Name</p>
                <p className="font-manrope font-medium">{application.full_name}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-1">Email</p>
                <p className="font-manrope font-medium">{application.email}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-1">Phone</p>
                <p className="font-manrope font-medium">{application.phone}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-1">City Preference</p>
                <p className="font-manrope font-medium">{application.city_preference}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-1">Skill Level</p>
                <p className="font-manrope font-medium capitalize">{application.skill_level}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-1">Availability</p>
                <p className="font-manrope font-medium">{application.availability_dates}</p>
              </div>
            </div>
            {application.portfolio_links && (
              <div className="mt-6">
                <p className="text-sm font-manrope text-gray-400 mb-1">Portfolio Links</p>
                <a href={application.portfolio_links} target="_blank" rel="noopener noreferrer" className="font-manrope text-accent hover:underline">
                  {application.portfolio_links}
                </a>
              </div>
            )}
          </div>

          <div className="bg-card p-6 border border-border">
            <h2 className="text-2xl font-unbounded font-bold mb-6">Goals & Background</h2>
            <div className="space-y-6">
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-2">Goals for the Program</p>
                <p className="font-manrope">{application.goals}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-2">Why AI House?</p>
                <p className="font-manrope">{application.why_ai_house}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-2">What will you build?</p>
                <p className="font-manrope">{application.what_will_you_build}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400 mb-2">Team or Solo?</p>
                <p className="font-manrope capitalize">{application.team_or_solo}</p>
              </div>
            </div>
          </div>

          {(application.resume_url || application.pitch_deck_url) && (
            <div className="bg-card p-6 border border-border">
              <h2 className="text-2xl font-unbounded font-bold mb-6">Attachments</h2>
              <div className="space-y-4">
                {application.resume_url && (
                  <div>
                    <p className="text-sm font-manrope text-gray-400 mb-1">Resume</p>
                    <a href={application.resume_url} target="_blank" rel="noopener noreferrer" className="font-manrope text-accent hover:underline">
                      {application.resume_url}
                    </a>
                  </div>
                )}
                {application.pitch_deck_url && (
                  <div>
                    <p className="text-sm font-manrope text-gray-400 mb-1">Pitch Deck</p>
                    <a href={application.pitch_deck_url} target="_blank" rel="noopener noreferrer" className="font-manrope text-accent hover:underline">
                      {application.pitch_deck_url}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-card p-6 border border-border">
            <h2 className="text-2xl font-unbounded font-bold mb-6">Status & Notes</h2>
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-2" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="shortlisted">Shortlisted</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Internal Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  className="mt-2"
                  placeholder="Add notes about this application..."
                  data-testid="notes-textarea"
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-accent text-white px-6 py-3 font-bold uppercase"
                data-testid="save-button"
              >
                <Save size={20} className="mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>

          <div className="bg-card p-6 border border-border">
            <h2 className="text-2xl font-unbounded font-bold mb-4">Metadata</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-manrope text-gray-400">Application ID</p>
                <p className="font-mono text-xs">{application.id}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400">Submitted</p>
                <p className="font-manrope text-sm">{new Date(application.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm font-manrope text-gray-400">Last Updated</p>
                <p className="font-manrope text-sm">{new Date(application.updated_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
